'use client';

import React, { forwardRef, ForwardedRef, useId } from 'react';
import classNames from 'classnames';
import './css/CustomCheckboxGroup.scss';
import { Helper } from '../Helper';
import { Error } from '../Error';
import { CustomCheckboxProps } from '../CustomCheckbox/CustomCheckbox';

export interface CustomCheckboxGroupProps
  extends React.HTMLAttributes<HTMLElement> {
  disabled?: boolean;
  error?: string;
  helper?: string;
  isErrorAnnounced?: boolean;
  isStacked?: boolean;
  legend?: string;
  name?: string;
  required?: boolean;
}

export const CustomCheckboxGroup = forwardRef(
  (props: CustomCheckboxGroupProps, ref: ForwardedRef<HTMLFieldSetElement>) => {
    const {
      className,
      disabled,
      error,
      helper,
      isErrorAnnounced = true,
      isStacked,
      legend,
      name: nameProp,
      required,
      children,
      ...spreadProps
    } = props;

    const uniqueId = useId();
    const name = nameProp || `${uniqueId}-custom-checkbox-group`;
    const errorId = error ? `${uniqueId}-error` : '';
    const helperId = helper ? `${uniqueId}-helper` : '';
    const ariaDescribedByValue = `${errorId} ${helperId}`.trim() || null;

    return (
      <fieldset
        {...spreadProps}
        className={classNames({
          'ps-custom-checkbox-group': true,
          'ps-custom-checkbox-group-error': Boolean(error),
          'ps-custom-checkbox-group-is-stacked': isStacked,
          [`${className}`]: className,
        })}
        disabled={disabled}
        ref={ref}
      >
        <legend>{legend}</legend>

        {helper && (
          <Helper
            id={helperId}
            className="ps-custom-checkbox-group-helper"
          >
            {helper}
          </Helper>
        )}

        <div className="ps-custom-checkbox-group-content">
          {React.Children.map(children, (child) => {
            if (!React.isValidElement(child)) {
              return child;
            }
            return React.cloneElement(child, {
              // @ts-expect-error - ts is not recognizing this attribute on the child element
              'aria-describedby': ariaDescribedByValue,
              name,
              'disabled':
                disabled || (child.props as CustomCheckboxProps).disabled,
              'required':
                required || (child.props as CustomCheckboxProps).required,
              'hasError': !!error,
            });
          })}
        </div>
        {error && (
          <Error
            id={errorId}
            className="ps-custom-checkbox-group-error"
            isErrorAnnounced={isErrorAnnounced}
          >
            {error}
          </Error>
        )}
      </fieldset>
    );
  }
);
